import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Receipt, 
  FolderKanban, 
  Building2,
  PoundSterling,
  ExternalLink,
  AlertTriangle,
  Check,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Job Special Action Buttons
 * - Chargeable: Mark job as chargeable to resident with 30% markup
 * - Project: Link job to a project
 * - Capex: Flag job as Capital Expenditure
 */

export default function JobActionButtons({ job, onUpdate }) {
  const [showChargeableDialog, setShowChargeableDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showCapexDialog, setShowCapexDialog] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Chargeable form state
  const [chargeableData, setChargeableData] = useState({
    resident_name: '',
    resident_email: '',
    resident_phone: '',
    resident_address: '',
    markup_percent: 30,
    notes: '',
    send_invoice: true
  });

  // Capex form state
  const [capexData, setCapexData] = useState({
    is_capex: true,
    capex_category: '',
    capex_notes: ''
  });

  const [selectedProjectId, setSelectedProjectId] = useState('');

  const jobId = job?.id || job?._id || job?.job_reference;
  const jobRef = job?.job_reference || job?.id;
  const quoteAmount = job?.approved_quote_amount || job?.quote_amount || 0;
  const calculatedCharge = quoteAmount * (1 + chargeableData.markup_percent / 100);

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/phs/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(res.data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleMarkChargeable = async () => {
    if (!chargeableData.resident_name || !chargeableData.resident_email) {
      toast.error('Please fill in resident name and email');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/phs/jobs/${jobId}/chargeable`,
        chargeableData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        <div>
          <p className="font-semibold">Invoice Created</p>
          <p className="text-sm">£{res.data.invoice?.total_amount?.toFixed(2)} - {chargeableData.resident_name}</p>
        </div>
      );

      setShowChargeableDialog(false);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkProject = async () => {
    if (!selectedProjectId) {
      toast.error('Please select a project');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/phs/projects/${selectedProjectId}/link-job`,
        { job_id: jobId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const project = projects.find(p => p.id === selectedProjectId);
      toast.success(`Linked to project: ${project?.name || selectedProjectId}`);
      setShowProjectDialog(false);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link project');
    } finally {
      setLoading(false);
    }
  };

  const handleFlagCapex = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/phs/jobs/${jobId}/capex`,
        capexData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(capexData.is_capex ? 'Flagged as CAPEX' : 'CAPEX flag removed');
      setShowCapexDialog(false);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update CAPEX flag');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Status Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        {job?.is_chargeable && (
          <Badge className="bg-purple-100 text-purple-700 border-purple-300">
            <Receipt className="h-3 w-3 mr-1" />
            Chargeable to Resident
          </Badge>
        )}
        {job?.is_project_job && (
          <Badge className="bg-blue-100 text-blue-700 border-blue-300">
            <FolderKanban className="h-3 w-3 mr-1" />
            Project: {job.project_name}
          </Badge>
        )}
        {job?.is_capex && (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
            <Building2 className="h-3 w-3 mr-1" />
            CAPEX {job.capex_category && `(${job.capex_category})`}
          </Badge>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <TooltipProvider>
          {/* Chargeable Button */}
          {!job?.is_chargeable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowChargeableDialog(true)}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="chargeable-btn"
                >
                  <Receipt className="h-4 w-4 mr-1" />
                  Chargeable
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Mark as chargeable to resident (30% markup)</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Project Button */}
          {!job?.is_project_job && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    loadProjects();
                    setShowProjectDialog(true);
                  }}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="project-btn"
                >
                  <FolderKanban className="h-4 w-4 mr-1" />
                  Project
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Link this job to a project</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Capex Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCapexData({ is_capex: !job?.is_capex, capex_category: job?.capex_category || '', capex_notes: job?.capex_notes || '' });
                  setShowCapexDialog(true);
                }}
                className={job?.is_capex 
                  ? "border-amber-500 bg-amber-50 text-amber-700" 
                  : "border-amber-300 text-amber-700 hover:bg-amber-50"
                }
                data-testid="capex-btn"
              >
                <Building2 className="h-4 w-4 mr-1" />
                {job?.is_capex ? 'CAPEX ✓' : 'Capex'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{job?.is_capex ? 'Update or remove CAPEX flag' : 'Flag as Capital Expenditure'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Chargeable Dialog */}
      <Dialog open={showChargeableDialog} onOpenChange={setShowChargeableDialog}>
        <DialogContent className="max-w-md" data-testid="chargeable-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-purple-600" />
              Charge to Resident
            </DialogTitle>
            <DialogDescription>
              Create an invoice for the end client with a {chargeableData.markup_percent}% markup
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Financial Summary */}
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex justify-between text-sm mb-1">
                <span>Contractor Cost:</span>
                <span className="font-medium">£{quoteAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>Markup ({chargeableData.markup_percent}%):</span>
                <span className="font-medium">£{(quoteAmount * chargeableData.markup_percent / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold pt-2 border-t border-purple-200">
                <span>Client Charge:</span>
                <span className="text-purple-700">£{calculatedCharge.toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Label>Resident Name *</Label>
              <Input
                value={chargeableData.resident_name}
                onChange={(e) => setChargeableData({...chargeableData, resident_name: e.target.value})}
                placeholder="John Smith"
                data-testid="resident-name-input"
              />
            </div>

            <div>
              <Label>Resident Email *</Label>
              <Input
                type="email"
                value={chargeableData.resident_email}
                onChange={(e) => setChargeableData({...chargeableData, resident_email: e.target.value})}
                placeholder="john@example.com"
                data-testid="resident-email-input"
              />
            </div>

            <div>
              <Label>Resident Phone</Label>
              <Input
                value={chargeableData.resident_phone}
                onChange={(e) => setChargeableData({...chargeableData, resident_phone: e.target.value})}
                placeholder="07123 456789"
              />
            </div>

            <div>
              <Label>Markup Percentage</Label>
              <Input
                type="number"
                value={chargeableData.markup_percent}
                onChange={(e) => setChargeableData({...chargeableData, markup_percent: parseFloat(e.target.value) || 30})}
                min={0}
                max={100}
              />
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={chargeableData.notes}
                onChange={(e) => setChargeableData({...chargeableData, notes: e.target.value})}
                placeholder="Additional notes for the invoice..."
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-teal-50 rounded-lg text-sm">
              <ExternalLink className="h-4 w-4 text-teal-600" />
              <span>Invoice will include a link to <strong>parkhomesupport.co.uk/pay-invoice</strong></span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChargeableDialog(false)}>Cancel</Button>
            <Button
              onClick={handleMarkChargeable}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-chargeable-btn"
            >
              {loading ? 'Creating...' : `Create Invoice (£${calculatedCharge.toFixed(2)})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="max-w-md" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderKanban className="h-5 w-5 text-blue-600" />
              Link to Project
            </DialogTitle>
            <DialogDescription>
              Associate this job with a project for tracking and reporting
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label>Select Project</Label>
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger data-testid="select-project">
                <SelectValue placeholder="Choose a project..." />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    <div className="flex items-center gap-2">
                      <FolderKanban className="h-4 w-4 text-blue-500" />
                      <span>{project.name}</span>
                      {project.site_name && (
                        <span className="text-gray-400 text-xs">({project.site_name})</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {projects.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                <p>No projects found.</p>
                <Button
                  variant="link"
                  onClick={() => window.location.href = '/projects'}
                  className="text-blue-600"
                >
                  Create a project first
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectDialog(false)}>Cancel</Button>
            <Button
              onClick={handleLinkProject}
              disabled={loading || !selectedProjectId}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-link-project-btn"
            >
              {loading ? 'Linking...' : 'Link to Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Capex Dialog */}
      <Dialog open={showCapexDialog} onOpenChange={setShowCapexDialog}>
        <DialogContent className="max-w-md" data-testid="capex-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-amber-600" />
              {capexData.is_capex ? 'Flag as CAPEX' : 'Remove CAPEX Flag'}
            </DialogTitle>
            <DialogDescription>
              CAPEX jobs appear in accounting reports for capital expenditure tracking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {capexData.is_capex && (
              <>
                <div>
                  <Label>CAPEX Category</Label>
                  <Select
                    value={capexData.capex_category}
                    onValueChange={(v) => setCapexData({...capexData, capex_category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="building">Building Works</SelectItem>
                      <SelectItem value="equipment">Equipment</SelectItem>
                      <SelectItem value="infrastructure">Infrastructure</SelectItem>
                      <SelectItem value="renovation">Renovation</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={capexData.capex_notes}
                    onChange={(e) => setCapexData({...capexData, capex_notes: e.target.value})}
                    placeholder="Additional notes for accounting..."
                    rows={2}
                  />
                </div>
              </>
            )}

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600 inline mr-2" />
              {capexData.is_capex 
                ? 'This job will be flagged for capital expenditure reporting'
                : 'This will remove the CAPEX flag from this job'
              }
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCapexDialog(false)}>Cancel</Button>
            <Button
              onClick={handleFlagCapex}
              disabled={loading}
              className={capexData.is_capex ? "bg-amber-600 hover:bg-amber-700" : "bg-gray-600 hover:bg-gray-700"}
              data-testid="confirm-capex-btn"
            >
              {loading ? 'Saving...' : (capexData.is_capex ? 'Flag as CAPEX' : 'Remove CAPEX')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
